import React, { useEffect } from 'react';
import Bluebird from 'bluebird';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, generatePath, Link } from 'react-router-dom';
import { Box, Breadcrumbs, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { Delete, PersonAdd } from '@mui/icons-material';
import { confirmAlert } from 'react-confirm-alert';
import * as groupsActions from '../actions/groups';
import { Roles } from '../models/roles';
import { currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite } from '../services/auth';
import useActiveSite from '../hooks/useActiveSite';
import snackbarHelper from '../helpers/snackbarHelper';
import ConfirmationDialog from '../components/ConfirmationDialog';
import UsersTable from '../components/UsersTable';
import routes from '../routes';
import { getUserFullName } from '../helpers/user';

const GroupMembersView = ({ subTab: groupId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const activeSite = useActiveSite();

  const groups = useSelector(state => state.entities.groups);
  const group = groups.byId[groupId];
  const members = group?.users || [];

  const isSiteDirector = currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
  ]);

  useEffect(() => {
    if (groupId) {
      Bluebird.try(() => dispatch(groupsActions.fetchSiteGroup(activeSite.id, groupId))).catch(err =>
        snackbarHelper.error(err.message)
      );
    }
  }, [groupId, activeSite.id]);

  const handleRemoveMember = (userId, userName) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmationDialog
          title="Remove Member"
          message={`Remove ${userName} from "${group?.name}"?`}
          buttons={[
            { label: 'Cancel' },
            {
              label: 'Remove',
              color: 'secondary',
              onClick: () => {
                Bluebird.try(() =>
                  dispatch(groupsActions.removeGroupUser(activeSite.id, groupId, userId))
                )
                  .then(() => snackbarHelper.success('Member removed.'))
                  .catch(err => snackbarHelper.error(err.message));
              },
            },
          ]}
          closeDialog={onClose}
        />
      ),
    });
  };

  const renderAddUsersToolbar = isSiteDirector
    ? () => (
        <Tooltip title="Add users to this group">
          <Button
            size="small"
            startIcon={<PersonAdd />}
            variant="outlined"
            color="primary"
            onClick={() => history.push(generatePath(routes.groupAddUsers, { groupId }))}
          >
            Add Users
          </Button>
        </Tooltip>
      )
    : undefined;

  const renderRemoveAction = isSiteDirector
    ? user => (
        <Tooltip title={`Remove ${getUserFullName(user)} from group`}>
          <IconButton size="small" onClick={() => handleRemoveMember(user.id, getUserFullName(user))}>
            <Delete fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    : null;

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Typography color="text.secondary">Users</Typography>
        <Link
          to={generatePath(routes.allUsers, { tab: 'groups' })}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          Groups
        </Link>
        <Typography color="text.primary">{group?.name || 'Loading…'}</Typography>
      </Breadcrumbs>

      <UsersTable
        tableId={`#groups/members-${groupId}`}
        title={group ? `${group.name} — Members` : 'Members'}
        users={members}
        site={activeSite}
        customToolbar={renderAddUsersToolbar}
        customRowActions={renderRemoveAction}
        showRolesColumn
        showVerifiedColumn={false}
        showEditUserButton={false}
        showDeleteUserButton={false}
        showDeleteSiteCoordinatorButton={false}
        showSentInvitationUserButton={false}
        showSelectableRows={false}
        showSendEmailButton={false}
        showSendSmsButton={false}
        showImpersonateUserButton={false}
        handleShowUsersWithAllSelectedRoles={() => {}}
      />
    </Box>
  );
};

export default GroupMembersView;
